/**
 * Shared back-navigation primitives for the enquiry pipeline.
 *
 * The pipeline has six sibling segment routes. Opening an enquiry should remember which
 * segment it was opened from so the detail back button can return there. The originating
 * segment is stamped onto the detail URL as a `?from=<segment>` query param (so it survives
 * a refresh) and read back when navigating away. When the origin is unknown — a deep link,
 * a notification, or a tile rendered outside the pipeline (customer profile, staff view) —
 * callers fall back to the default segment so back never dumps the user to the home page or
 * the lead-import inbox.
 */

/** Base path for the supplier enquiries feature. */
export const ENQUIRIES_BASE_PATH = '/su/me/enquiries';

/** The six pipeline segment routes an enquiry can be opened from. */
export const ENQUIRY_SEGMENTS = ['active-lead', 'quoted', 'selected', 'booked', 'completed', 'archived'] as const;

export type EnquirySegment = (typeof ENQUIRY_SEGMENTS)[number];

/** Segment the detail and edit back buttons return to when the origin is unknown. */
export const DEFAULT_ENQUIRY_SEGMENT: EnquirySegment = 'active-lead';

/** Query-param key used to stamp the originating pipeline segment onto the detail URL. */
export const ENQUIRY_FROM_PARAM = 'from';

/** Absolute fallback destination shared by every enquiry back button when no history exists. */
export const DEFAULT_ENQUIRY_FALLBACK = `${ENQUIRIES_BASE_PATH}/${DEFAULT_ENQUIRY_SEGMENT}`;

/** Type guard for a candidate segment string (e.g. read from a query param). */
export function isEnquirySegment(value: string | null | undefined): value is EnquirySegment {
  return !!value && (ENQUIRY_SEGMENTS as readonly string[]).includes(value);
}

/**
 * Extracts the pipeline segment from a router URL when it points at one of the six sibling
 * segment routes (e.g. `/su/me/enquiries/quoted` or `/su/me/enquiries/active-lead/your-enquiries`).
 * Returns null for any other location — including the enquiry detail route itself and the
 * customer/staff views that also list enquiries — so the caller falls back to the default.
 */
export function originSegmentFromUrl(url: string | null | undefined): EnquirySegment | null {
  if (!url) return null;
  // Drop query string / fragment, then split into non-empty path segments.
  const parts = url.split(/[?#]/)[0].split('/').filter(Boolean);
  const enquiriesIndex = parts.indexOf('enquiries');
  if (enquiriesIndex === -1) return null;
  const segment = parts[enquiriesIndex + 1];
  return isEnquirySegment(segment) ? segment : null;
}
